import { apiClient } from '@/core/api/client';
import type {
  FpQaEngagementDetail,
  FpQaEngagementSummary,
  RenameFpQaEngagementPayload,
  ShareFpQaEngagementPayload,
  ShareFpQaEngagementResult,
} from '../types/fp-qa.types';

// Note: no createFpQaEngagement helper here on purpose. The engagement is
// created from ChatInterface at the moment the navigator Q&A completes —
// it posts directly via apiClient.post so it can send the chatbot's
// classification fields (careStageName, summaryBullets, …) without a
// duplicated payload type. The dashboard never creates engagements
// directly, so a typed helper would only be dead code.

const BASE = '/financial-advisors/me/qa-engagements';

export async function listFpQaEngagements(): Promise<FpQaEngagementSummary[]> {
  const res = await apiClient.get<FpQaEngagementSummary[]>(BASE);
  return res.data;
}

export async function getFpQaEngagement(id: string): Promise<FpQaEngagementDetail> {
  const res = await apiClient.get<FpQaEngagementDetail>(`${BASE}/${id}`);
  return res.data;
}

export async function renameFpQaEngagement(
  id: string,
  payload: RenameFpQaEngagementPayload,
): Promise<FpQaEngagementDetail> {
  const res = await apiClient.patch<FpQaEngagementDetail>(`${BASE}/${id}`, payload);
  return res.data;
}

/**
 * Share a completed care plan with a client by email and/or in-app. The backend
 * emails the PDF (email mode) or sends an in-app notification (in-app mode) and,
 * for an eligible Care Seeker recipient, publishes the plan to their dashboard.
 */
export async function shareFpQaEngagement(
  id: string,
  payload: ShareFpQaEngagementPayload,
): Promise<ShareFpQaEngagementResult> {
  const res = await apiClient.post<ShareFpQaEngagementResult>(`${BASE}/${id}/share`, payload);
  return res.data;
}

/**
 * Fetch the engagement PDF as an authenticated blob, then trigger a browser
 * download via an in-memory object URL. Fetching as a blob is required
 * because the FE talks to the backend cross-origin, so the HTML `download`
 * attribute would be ignored on a plain anchor.
 *
 * Returns the suggested filename the backend sent back (from the
 * Content-Disposition header) so callers can surface it in a toast / aria.
 */
export async function downloadFpQaEngagementPdf(
  id: string,
  fallbackFilename = 'careplan.pdf',
): Promise<string> {
  const res = await apiClient.get<Blob>(`${BASE}/${id}/pdf`, {
    responseType: 'blob',
  });
  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data as ArrayBuffer], { type: 'application/pdf' });

  const disposition =
    (res.headers as Record<string, string | undefined>)['content-disposition'] ?? '';
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackFilename;

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Defer revoke so the browser has a tick to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);

  return filename;
}
